/**
 * `PlatformDevice` on Electron (PLAN §5.3) — the shell half, owned by T3.A.
 *
 * This is the first of the four bridge surfaces to become real. The capability
 * answers (registration type, permission prompts, OTA, version policy) still
 * come from the capability table; only the *shell* methods route through the
 * preload.
 *
 * T3.C extends this file with the device-identity and pairing work (Q5/A4);
 * nothing here needs to change for that.
 */
import type { RuntimeCapabilities } from '../types';
import { RUNTIME_CAPABILITIES } from '../capabilities';
import { shellBridge, unwrapBridgeResult } from './bridge';
import type { PlatformDevice, PlatformShellInfo } from '../services/types';

const capabilities: RuntimeCapabilities = RUNTIME_CAPABILITIES.electron;

export function createElectronDevice(): PlatformDevice {
  return {
    registrationType: capabilities.deviceRegistrationType,
    platformTag: 'electron',
    osPermissionPrompts: capabilities.osPermissionPrompts,
    otaUpdates: capabilities.otaUpdates,
    remoteVersionPolicy: capabilities.remoteVersionPolicy,
    deviceDiagnostics: capabilities.deviceDiagnostics,
    batteryTelemetry: capabilities.batteryTelemetry,
    wifiManagement: capabilities.wifiManagement,

    /**
     * Read by the boot diagnostics popup. Distinguishing a desktop register
     * from a browser tab in a support payload is the whole point of the
     * `electron`/`chromium`/`userDataPath` fields — on the web those questions
     * have no answer, and today a desktop user would look identical to one.
     */
    getShellInfo: async (): Promise<PlatformShellInfo> => {
      const info = unwrapBridgeResult('shell', await shellBridge().getInfo({}));
      return {
        runtime: 'electron',
        appVersion: info.appVersion,
        electron: info.electron,
        chromium: info.chromium,
        userDataPath: info.userDataPath,
        osPlatform: info.osPlatform,
        computerName: info.computerName,
      };
    },

    /**
     * The OS browser, never an Electron window. The scheme allow-list is
     * enforced in the main process as well as here, because `security.ts` also
     * routes blocked in-app navigations through the same function — one
     * gatekeeper, not two that can drift.
     */
    openExternal: async (url: string): Promise<void> => {
      unwrapBridgeResult('shell', await shellBridge().openExternal({ url }));
    },

    /**
     * Destroys shell-owned data for `reset-app-data.ts`. On desktop that is the
     * `safeStorage`-encrypted credential document, which lives outside the
     * renderer's reach — so without this channel a "reset app data" would leave
     * the device session and provider credentials behind.
     */
    clearShellAppData: async (): Promise<void> => {
      unwrapBridgeResult('shell', await shellBridge().clearSecureStorage({}));
    },

    /**
     * The renderer has torn itself down; main runs its own disposers and exits
     * with code 0, which PointCraft Home reads as a normal close.
     */
    quitApp: async (reason): Promise<void> => {
      unwrapBridgeResult('shell', await shellBridge().quit({ reason }));
    },

    answerCloseRequest: async (requestId, answer, reason): Promise<void> => {
      unwrapBridgeResult(
        'shell',
        await shellBridge().answerCloseRequest({ requestId, answer, ...(reason ? { reason } : {}) })
      );
    },

    onCloseRequested: (listener) =>
      shellBridge().onCloseRequested(({ requestId }) => listener(requestId)),

    /**
     * The file PointCraft Home reads. Main picks the path and stamps the
     * process facts; the reply arrives only after main has read the file back
     * with this update in it, so a resolved promise is a confirmed file.
     */
    publishSessionFile: async (update) =>
      unwrapBridgeResult('shell', await shellBridge().publishSessionFile(update)),
  };
}
